import {
  ExerciseCanvas,
  ExerciseVideoCard,
  drawCardFrame,
  drawChecklistPanel,
  drawPhasePill,
} from "@/components/we360/ExerciseCanvas";
import { useUserProfile } from "@/contexts/UserProfileContext";

const PURPLE = "#883476";
const PURPLE_LIGHT = "#F3E5F0";

function useIsFemale(sexProp) {
  let ctxSex = null;
  try {
    const ctx = useUserProfile();
    ctxSex = ctx?.profile?.sexo || ctx?.profile?.genero;
  } catch {
    // Allow rendering outside UserProfileProvider in isolated unit tests
  }
  const resolved = sexProp || ctxSex || "";
  return /^femenino|^mujer|^female|^f$/i.test(String(resolved).trim());
}

export function MeditationSVG({ sexo }) {
  const isFemale = useIsFemale(sexo);
  return (
    <ExerciseVideoCard
      testId="f5-meditation-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_f5_ansiedad_f.mp4"
          : "/videos/exercises/we360_ai_f5_ansiedad.mp4"
      }
      title="Bienestar Emocional y Salud Mental (F5)"
      subtitle="Espacio confidencial para evaluar tu equilibrio emocional y descanso"
      badgeText="Confidencial"
      checklistTitle="Dimensiones F5"
      tips={[
        { label: "Estrés y Energía", sub: "Carga percibida en el mes", active: true },
        { label: "Ánimo y Vitalidad", sub: "Índice OMS de bienestar", active: true },
        { label: "100% Confidencial", sub: "Uso exclusivo de salud", active: true },
      ]}
      phaseLabel="Responde según cómo te has sentido durante las últimas 2 a 4 semanas"
      isHighlight={true}
    />
  );
}

export function StressSVG({ sexo }) {
  const isFemale = useIsFemale(sexo);
  return (
    <ExerciseVideoCard
      testId="f5-stress-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_f5_estres_f.mp4"
          : "/videos/exercises/we360_ai_f5_estres.mp4"
      }
      title="Manejo del Estrés y Agotamiento"
      subtitle="Frecuencia con la que has sentido sobrecarga o fatiga emocional"
      badgeText="Último mes"
      checklistTitle="Señales Evaluadas"
      tips={[
        { label: "Control percibido", sub: "Capacidad ante imprevistos", active: true },
        { label: "Carga mental", sub: "Sensación de saturación", active: true },
        { label: "Fatiga laboral", sub: "Recuperación tras jornada", active: true },
      ]}
      phaseLabel="Selecciona la frecuencia que mejor describa tu experiencia reciente"
      isHighlight={true}
    />
  );
}

export function WellbeingSVG({ sexo }) {
  const isFemale = useIsFemale(sexo);
  return (
    <ExerciseVideoCard
      testId="f5-wellbeing-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_f5_bienestar_f.mp4"
          : "/videos/exercises/we360_ai_f5_bienestar.mp4"
      }
      title="Vitalidad, Ánimo y Red de Apoyo"
      subtitle="Energía diaria, calidad del descanso y acompañamiento social"
      badgeText="Bienestar"
      checklistTitle="Pilares de Bienestar"
      tips={[
        { label: "Buen humor y calma", sub: "Tranquilidad en el día", active: true },
        { label: "Sueño reparador", sub: "Despertar con energía", active: true },
        { label: "Apoyo cercano", sub: "Familia, amigos o equipo", active: true },
      ]}
      phaseLabel="Evalúa tu vitalidad positiva y tus recursos de apoyo emocional"
      isHighlight={true}
    />
  );
}

export function CommentsSVG({ sexo }) {
  const isFemale = useIsFemale(sexo);
  return (
    <ExerciseVideoCard
      testId="f5-comments-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_f5_reflexion_f.mp4"
          : "/videos/exercises/we360_ai_f5_reflexion.mp4"
      }
      title="Tu Espacio Seguro y Confidencial"
      subtitle="Comparte cualquier comentario adicional o solicitud de orientación"
      badgeText="Opcional"
      checklistTitle="Privacidad Clínica"
      tips={[
        { label: "Dato protegido", sub: "Acceso solo profesional", active: true },
        { label: "Acompañamiento", sub: "Orientación personalizada", active: true },
        { label: "Campo libre", sub: "Opcional antes de enviar", active: true },
      ]}
      phaseLabel="Tus respuestas ayudan a personalizar tu plan de bienestar integral"
      isHighlight={true}
    />
  );
}

export function F5SummarySVG({ complete }) {
  return (
    <ExerciseCanvas
      cycleDurationMs={2800}
      testId="f5-summary-canvas"
      renderFrame={(ctx, t) => {
        const pulse = (1 - Math.cos(t * Math.PI * 2)) / 2;

        drawCardFrame(ctx, {
          title: "Resumen de Bienestar Emocional (F5)",
          subtitle: complete
            ? "¡Evaluación lista! Revisa tu balance emocional antes de guardar"
            : "Verifica el resumen de tus indicadores emocionales",
          badgeText: complete ? "Listo" : "Resumen",
          progress: complete ? 1 : t,
          isHighlight: Boolean(complete),
          accentColor: PURPLE,
          accentLight: PURPLE_LIGHT,
        });

        const cx = 205;
        const cy = 215;

        ctx.beginPath();
        ctx.arc(cx, cy, 68 + pulse * 6, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(136, 52, 118, 0.10)";
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 52, 0, Math.PI * 2);
        ctx.fillStyle = PURPLE;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 42, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();

        ctx.fillStyle = PURPLE;
        ctx.font = '800 16px "Plus Jakarta Sans", sans-serif';
        ctx.fillText("EMO", cx - 19, cy + 6);

        drawChecklistPanel(ctx, {
          title: "Síntesis Emocional",
          tips: [
            { label: "Nivel de Estrés", sub: "Escala percibida mensual", active: true },
            { label: "Índice de Bienestar", sub: "Vitalidad y ánimo diario", active: true },
            { label: "Equilibrio Global", sub: "Recomendación preventiva", active: Boolean(complete) },
          ],
        });

        drawPhasePill(ctx, {
          label: complete
            ? "✓ Presiona «Enviar evaluación» para sincronizar tu perfil emocional"
            : "Revisa tus resultados antes de finalizar",
          isHighlight: Boolean(complete),
          accentColor: PURPLE,
          accentLight: PURPLE_LIGHT,
        });
      }}
    />
  );
}

